import React from 'react';
import { createRoot } from 'react-dom/client';

import { BODY, HEADER_MAIN } from '@Ui/js/constant/dom';
import { addClass, hasClass, removeClass } from '@Ui/js/dom/classes';
import { on } from '@Ui/js/dom/events';
import {
  showOverlay,
  hideOverlay,
  getOverlayState
} from '@Ui/js/module/overlay';
import eventEmitter, { EventsTypes } from '@Ui/js/services/events/eventEmitter';

import UserMenu from 'common/components/Header/UserMenu';
import { IMAGE_EMPTY_AVATAR } from 'common/configuration/constants';
import { initRewindMenu } from 'common/module/ui/header-main';
import { isAuthenticatedViaFacebook } from 'common/services/authentication/tokens';
import loadFacebook from 'common/third_party/facebook';
import { getStateIndex } from 'common/tools/dom/mq-state';
import { requestAnimationFrame } from 'common/tools/functions/request-animation-frame';
import { getCroppedAvatar } from 'common/tools/images/avatars';
import {
  getImageUrl,
  removeScheme,
  addUrlParam
} from 'common/tools/network/url';
import { runAfterDesobfuscation } from 'common/tools/seo/desobfuscation';
import { ReduxUser } from 'common/types';

// private vars
const elemUserspaceNavSubContent = document.getElementsByClassName(
  'header-nav-userspace-subcontent'
)[0];

const OPEN_HEADER_USERSPACE_NAV_CLASS = 'open-header-userspace-nav';

let isUserspaceNavOpen = false;

function handleHeaderMobileUserspaceNav(mqStateMobile: number) {
  eventEmitter.on(EventsTypes.MENU_OPEN, () => {
    removeClass(BODY, OPEN_HEADER_USERSPACE_NAV_CLASS);
    isUserspaceNavOpen = false;
  });

  eventEmitter.on(EventsTypes.SEARCH_OPEN, () => {
    removeClass(BODY, OPEN_HEADER_USERSPACE_NAV_CLASS);
    isUserspaceNavOpen = false;
  });

  runAfterDesobfuscation(() => {
    const BTN_USERSPACE_MENU = document.getElementById(
      'header-nav-userspace-upper-link'
    );

    if (BTN_USERSPACE_MENU) {
      on(BTN_USERSPACE_MENU, 'click', (e: Event) => {
        if (!hasClass(BODY, 'logged-in') || getStateIndex() > mqStateMobile) {
          return false;
        }

        e.preventDefault();

        eventEmitter.emit(EventsTypes.MENU_CLOSE);
        eventEmitter.emit(EventsTypes.SEARCH_CLOSE);

        requestAnimationFrame(() => {
          if (!isUserspaceNavOpen) {
            isUserspaceNavOpen = true;
            addClass(BODY, OPEN_HEADER_USERSPACE_NAV_CLASS);
            if (!getOverlayState().open) {
              showOverlay();
            }
          } else {
            isUserspaceNavOpen = false;
            removeClass(BODY, OPEN_HEADER_USERSPACE_NAV_CLASS);
            if (getOverlayState().open) {
              hideOverlay();
            }
          }
        });
      });
    }
  });
}

const onUserAvatarError = (e: Event) => {
  (e.target as HTMLImageElement).src = getImageUrl({
    src: IMAGE_EMPTY_AVATAR
  });
};

export const updateUserMenu = async (user: ReduxUser) => {
  if (user.loggedIn && user.account) {
    const { label, picture } = user.account;
    const myAccountAvatar = document.getElementsByClassName(
      'js-myaccount-avatar'
    ) as HTMLCollectionOf<HTMLImageElement>;
    if (myAccountAvatar.length) {
      let avatarUrl = getCroppedAvatar(picture);
      if (isAuthenticatedViaFacebook()) {
        const authResponse = await loadFacebook();
        if (authResponse?.accessToken) {
          avatarUrl = addUrlParam(
            avatarUrl,
            'access_token',
            authResponse?.accessToken
          );
        }
      }
      avatarUrl = removeScheme(avatarUrl);
      for (const item of myAccountAvatar) {
        item.src = avatarUrl;
        item.addEventListener('error', onUserAvatarError);
        item.alt = label ?? '';
      }
    }

    addClass(BODY, 'logged-in');
    removeClass(BODY, 'logged-out');

    const root = createRoot(elemUserspaceNavSubContent);
    root.render(<UserMenu user={user} />);
  } else {
    addClass(BODY, 'logged-out');
    removeClass(BODY, 'logged-in');
  }

  initRewindMenu(user);
};

export default function init(mqStateMobile: number) {
  if (!HEADER_MAIN) {
    return false;
  }

  handleHeaderMobileUserspaceNav(mqStateMobile);
  initRewindMenu();
}
